import { AntDesign } from '@expo/vector-icons';
import { router } from 'expo-router';
import { useEffect, useState } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleSheet,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native';
import Animated from 'react-native-reanimated';

import { SeasonMission, SeasonMissionParticipant } from '@/api/models/seasonMissionModel';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { useColor } from '@/hooks/useColor';

import { useGetSeasonMissionParticipants } from '@/api/hooks/seasonMissionHooks';

import ListEmptyComponent from '../ListEmptyComponent';
import NextPageFetchingIndicator from '../NextPageFetchingIndicator';
import SeasonMissionCard from './SeasonMissionCard';
import SeasonMissionParticipantCard from './SeasonMissionParticipantCard';

interface SeasonMissionsListProps {
  missionsData: SeasonMission[] | undefined;
  refetch: () => void;
  isRefetching: boolean;
  onEndReached: () => void;
  isFetchingNextPage: boolean;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollEnabled?: boolean;
}

const SeasonMissionsList = ({
  missionsData,
  refetch,
  isRefetching,
  onEndReached,
  isFetchingNextPage,
  onScroll,
  scrollEnabled
}: SeasonMissionsListProps) => {
  const { width } = useWindowDimensions();
  const colors = useColor();

  const [numOfColumns, setNumOfColumns] = useState(3);
  const [listWidth, setListWidth] = useState<number>(width);
  const [selectedMission, setSelectedMission] = useState<SeasonMission | null>(null);

  const cardWidth = (listWidth - (numOfColumns - 1) * style.flatListColumnWrapperStyle.gap) / numOfColumns;

  const { data: participantsData, isFetching: isFetchingParticipants } = useGetSeasonMissionParticipants(
    selectedMission?.id || ''
  );

  useEffect(() => {
    if (width > 820 && numOfColumns !== 3) {
      setNumOfColumns(3);
    } else if (width > 640 && width < 820 && numOfColumns !== 2) {
      setNumOfColumns(2);
    } else if (width < 640 && numOfColumns !== 1) {
      setNumOfColumns(1);
    }
  }, [numOfColumns, width]);

  if (selectedMission) {
    return (
      <View
        ref={(node) => {
          if (node) node.measureInWindow((x, y, w, h) => setListWidth(w));
        }}
        style={style.container}>
        <TouchableOpacity
          style={style.backButton}
          onPress={() => setSelectedMission(null)}
          activeOpacity={0.7}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <AntDesign name="left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Animated.FlatList
          key={numOfColumns}
          onScroll={onScroll}
          scrollEnabled={scrollEnabled}
          style={style.flatList}
          {...(numOfColumns > 1 && {
            columnWrapperStyle: style.flatListColumnWrapperStyle
          })}
          contentContainerStyle={style.flatListContentContainerStyle}
          horizontal={false}
          numColumns={numOfColumns}
          keyExtractor={(item: SeasonMissionParticipant) => item.id}
          data={participantsData}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }: { item: SeasonMissionParticipant }) => {
            return (
              <SeasonMissionParticipantCard
                title={item.name}
                description={item.description}
                width={cardWidth}
                height={300}
                image={firebaseImage(item.thumbnail?.at(0), ThumbSize.Large)}
                onPress={() =>
                  router.push({
                    pathname: '/npoDetail/[npoId]/insights',
                    params: { npoId: item.nonProfitOrgId }
                  })
                }
              />
            );
          }}
          ListEmptyComponent={
            isFetchingParticipants ? null : (
              <ListEmptyComponent
                label={i18n.t('home.empty_participants', { defaultValue: 'No participants available.' })}
              />
            )
          }
          ListFooterComponent={isFetchingParticipants ? <NextPageFetchingIndicator /> : null}
        />
      </View>
    );
  }

  return (
    <View
      ref={(node) => {
        if (node) node.measureInWindow((x, y, w, h) => setListWidth(w));
      }}
      style={style.container}>
      <Animated.FlatList
        key={numOfColumns}
        onScroll={onScroll}
        scrollEnabled={scrollEnabled}
        style={style.flatList}
        {...(numOfColumns > 1 && {
          columnWrapperStyle: style.flatListColumnWrapperStyle
        })}
        contentContainerStyle={style.flatListContentContainerStyle}
        horizontal={false}
        numColumns={numOfColumns}
        keyExtractor={(item: SeasonMission) => item.id}
        data={missionsData}
        showsVerticalScrollIndicator={false}
        onRefresh={refetch}
        refreshing={isRefetching}
        onEndReachedThreshold={0.1}
        onEndReached={onEndReached}
        renderItem={({ item }: { item: SeasonMission }) => {
          return (
            <SeasonMissionCard
              title={item.title}
              subtitle={item.subtitle}
              description={item.description || ''}
              height={500}
              width={cardWidth}
              image={firebaseImage(item.thumbnail?.at(0))}
              onPress={() => setSelectedMission(item)}
            />
          );
        }}
        ListEmptyComponent={<ListEmptyComponent label={i18n.t('home.empty_missions')} />}
        ListFooterComponent={isFetchingNextPage ? <NextPageFetchingIndicator /> : null}
      />
    </View>
  );
};

export default SeasonMissionsList;

const style = StyleSheet.create({
  container: {
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },

  flatList: {
    width: '100%',
    marginTop: 26,
    flex: 1
  },

  backButton: {
    marginTop: 16,
    marginBottom: 16,
    alignSelf: 'flex-start',
    justifyContent: 'center',
    alignItems: 'center'
  },

  //Vertical Gap for flatList
  flatListContentContainerStyle: {
    flex: 1,
    gap: 27
  },

  //Horizontal Gap for flatList
  flatListColumnWrapperStyle: {
    gap: 22,
    justifyContent: 'center'
  }
});
